import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useCallback, useMemo, useState } from 'react';

import templateFile from '@/assets/Budget_Upload_Template.xlsx?url';
import DownloadButton from '@/components/shared/Buttons/DownloadButton';
import UploadButton from '@/components/shared/Buttons/UploadButton';
import BaseTable from '@/components/shared/Table/BaseTable';
import useBaseTable from '@/components/shared/Table/hooks/useBaseTable';
import { buildApiUrl } from '@/config/runtimeConfig';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { checkIsImportActive } from '@/hooks/importPolling';
import { ImportedBudgetGroupFileHistoryItem, useImportedBudgetFileHistory } from '@/hooks/useImportedBudgetFileHistory';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useSnackbar } from '@/hooks/useSnackbar';
import { DEFAULT_PAGE_SIZE, useTablePaginationState } from '@/hooks/useTablePaginationState';
import { fetchAndDownload, triggerStaticDownload } from '@/shared/api/downloadFile';
import type { components } from '@/shared/api/openapi';
import { handleSnackbarError } from '@/shared/utils/errorHandler';

const columnHelper = createColumnHelper<ImportedBudgetGroupFileHistoryItem>();
type ImportUsersBudgetGroupsResponse = components['schemas']['ImportUsersBudgetGroupsEndpoint_Response'];
type TrackedImport = { organizationId: number; importId: number };

const ImportBudgetGroupRelationships = () => {
  const snackbar = useSnackbar();
  const { showSuccess } = snackbar;
  const [trackedImport, setTrackedImport] = useState<TrackedImport | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const { organizationId } = useOrganizationId();
  const trackedImportId = trackedImport?.organizationId === organizationId ? trackedImport.importId : null;
  const { organizations, isLoading: organizationsLoading } = useOrganizations();
  const { pagination, apiPage, setPagination } = useTablePaginationState({
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });
  const {
    importHistory,
    isLoading: importHistoryLoading,
    refetch: refetchImportHistory,
  } = useImportedBudgetFileHistory(organizationId, trackedImportId, apiPage, pagination.pageSize);
  const { getAccessToken } = useAuth();

  const formatDateTime = useCallback((dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return dateString || '-';
    }
  }, []);

  const handleUploadFile = () => {
    if (!organizationId || organizationId === 0) {
      snackbar.showError('Please select an organization first');
      return;
    }

    if (organizationsLoading) {
      snackbar.showError('Please wait while organizations are loading');
      return;
    }

    if (!organizations || organizations.length === 0) {
      snackbar.showError('Organizations not available. Please refresh the page.');
      return;
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Validate file type
    if (!file.name.endsWith('.xlsx')) {
      snackbar.showError('Only .xlsx files are allowed');
      event.target.value = '';
      return;
    }

    try {
      setIsUploading(true);
      if (organizationsLoading || !organizations) {
        snackbar.showError('Organizations are still loading. Please try again.');
        event.target.value = '';
        return;
      }

      const selectedOrg = organizations.find((org) => org.id === organizationId);

      if (!selectedOrg) {
        snackbar.showError('Organization not found. Please select an organization and try again.');
        event.target.value = '';
        return;
      }

      const organizationCode = (selectedOrg as { organizationCode?: string | null }).organizationCode;

      if (!organizationCode) {
        snackbar.showError('Organization code not available. Please refresh the page.');
        event.target.value = '';
        return;
      }

      const token = await getAccessToken();
      const url = buildApiUrl(`/api/organizations/${organizationId}/budget-groups/import`);

      const formData = new FormData();
      formData.append('file', file, file.name);

      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        let errorMessage = 'Failed to upload file';
        try {
          errorMessage = await response.json();
        } catch (err) {
          console.error(err);
        }
        handleSnackbarError(response, snackbar, errorMessage);
        event.target.value = '';
        return;
      }

      const responseBody = (await response.json()) as ImportUsersBudgetGroupsResponse;
      if (responseBody.id == null) {
        throw new Error('Upload response did not include queued import id');
      }

      setTrackedImport({ organizationId, importId: responseBody.id });
      refetchImportHistory();
      showSuccess('File uploaded successfully');
      event.target.value = '';
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload file';
      handleSnackbarError(error, snackbar, errorMessage);
      event.target.value = '';
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      triggerStaticDownload(templateFile, 'Budget_Upload_Template.xlsx');
      showSuccess('Template downloaded successfully');
    } catch (error) {
      handleSnackbarError(error, snackbar, 'Failed to download template');
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, pageIndex: newPage }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPagination({ pageIndex: 0, pageSize: newPageSize });
  };

  const handleDownloadFile = useCallback(
    async (row: ImportedBudgetGroupFileHistoryItem) => {
      if (!row || !row.id) {
        snackbar.showError('File information not available');
        return;
      }

      const downloadLink = (row as { downloadLink?: string }).downloadLink;
      if (!downloadLink) {
        snackbar.showError('Download link not available');
        return;
      }

      try {
        await fetchAndDownload({
          url: buildApiUrl(
            `/api/organizations/${encodeURIComponent(organizationId)}/budget-groups/imported-file-history/${encodeURIComponent(row.id)}/download`,
          ),
          getToken: getAccessToken,
          fileName: row.fileName || 'download.xlsx',
        });

        showSuccess('File downloaded successfully');
      } catch (error) {
        handleSnackbarError(error, snackbar, 'Failed to download file');
      }
    },
    [getAccessToken, organizationId, showSuccess, snackbar],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => <span data-testid={`import-cell-status-${info.row.original.id}`}>{info.getValue() || '-'}</span>,
      }),
      columnHelper.accessor('createdBy', {
        header: 'Created By',
        cell: (info) => <span data-testid={`import-cell-createdBy-${info.row.original.id}`}>{info.getValue() || '-'}</span>,
      }),
      columnHelper.accessor('fileName', {
        header: 'File Name',
        cell: (info) => {
          const row = info.row.original;
          return (
            /* Create shorter file names if necessary */
            <Box
              sx={{
                maxWidth: '300px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              data-testid={`import-cell-fileName-${row.id}`}
            >
              <Tooltip title={row.fileName} arrow>
                <span>{row.fileName}</span>
              </Tooltip>
            </Box>
          );
        },
      }),
      columnHelper.accessor('createdOn', {
        header: 'Created On',
        cell: (info) => {
          return <Typography data-testid={`import-cell-createdOn-${info.row.original.id}`}>{formatDateTime(info.getValue())}</Typography>;
        },
      }),
      columnHelper.accessor('finishedOn', {
        header: 'Finished On',
        cell: (info) => {
          return <Typography data-testid={`import-cell-finishedOn-${info.row.original.id}`}>{formatDateTime(info.getValue())}</Typography>;
        },
      }),
      columnHelper.display({
        id: 'file',
        header: 'File',
        cell: (info) => {
          const row = info.row.original;
          return (
            <Tooltip title={row.status !== 'Completed with error' ? 'Download not available' : 'Download File'}>
              <span>
                <IconButton
                  onClick={() => handleDownloadFile(row)}
                  size='small'
                  sx={{ padding: '4px' }}
                  data-testid={`download-file-button-${row.id}`}
                  disabled={row.status !== 'Completed with error'}
                >
                  <FileDownloadOutlined />
                </IconButton>
              </span>
            </Tooltip>
          );
        },
      }),
    ],
    [formatDateTime, handleDownloadFile],
  );

  const isUploadProcessing = isUploading || checkIsImportActive(importHistory?.items ?? undefined, trackedImportId);

  const tableWithData = useBaseTable<ImportedBudgetGroupFileHistoryItem>({
    tableData: importHistory?.items ?? [],
    columns,
    hasPagination: true,
    isColumnOrderEnabled: false,
    isColumnVisibilityEnabled: false,
    isRowSelectionEnabled: false,
    pagination,
    onPaginationChange: setPagination,
    paginationCount: importHistory?.totalCount ?? 0,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [10, 50, 100],
  });

  return (
    <>
      <Stack sx={{ alignSelf: 'flex-end' }} spacing={2} direction='row'>
        <DownloadButton label='Download Template' onDownload={handleDownloadTemplate} />
        <UploadButton label='Upload File' onUpload={handleUploadFile} onFileChange={handleFileChange} isLoading={isUploadProcessing} />
      </Stack>
      <BaseTable<ImportedBudgetGroupFileHistoryItem>
        {...tableWithData}
        testId='import-budget-table'
        ariaLabel='Import Budget Group Relationships'
        noDataMessage='No import history found'
        loadingData={{ isLoading: importHistoryLoading, loadingMessage: 'Loading...' }}
        processing={{
          isProcessing: isUploadProcessing,
        }}
      />
    </>
  );
};

export default ImportBudgetGroupRelationships;
